import React, { useEffect, useMemo, useRef, useState } from "react";

import Icon from "@/assets/images/icons";
import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import ConfirmSubmitButton from "@/common/ConfirmSubmitButton/ConfirmSubmitButton";
import Text from "@/common/Text/Text";
import {
  useGetAssistantPackageStatusQuery,
  useGetPlayerPackageStatusQuery,
  useGetScreensaverPackageStatusQuery,
  useLazyGetAssistantPackageStatusQuery,
  useLazyGetPlayerPackageStatusQuery,
  useLazyGetScreensaverPackageStatusQuery,
  useRefreshSoftwareInventoryMutation,
} from "@/features/devices/devicesApi";
import {
  formatAvailableVersionLabel,
  formatInstalledVersionLabel,
  formatInventoryVerificationCaption,
  isSoftwareStatusUpToDate,
  isSoftwareUpdateEligible,
} from "@/features/devices/softwareInventoryModel";
import {
  shouldContinueSoftwareInventoryOtaSync,
  SOFTWARE_INVENTORY_OTA_POLL_MS,
  type SoftwareInventoryOtaSyncTarget,
} from "@/features/devices/softwareInventoryOtaSync";
import {
  createInventoryRequestGeneration,
  resolveSoftwareInventoryFleetPollingIntervalMs,
  shouldFetchSoftwareInventoryLane,
  SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS,
} from "@/features/devices/softwareInventoryPollingPolicy";
import {
  SOFTWARE_INVENTORY_REFRESH_COPY,
  newestInventoryObservedAt,
  runManualSoftwareInventoryRefresh,
} from "@/features/devices/softwareInventoryRefresh";
import { BrandSoftwareTypeOption } from "@/features/ui/types";
import { SyncOutlined } from "@ant-design/icons";
import { Select } from "antd";
import classNames from "classnames";
import toast from "react-hot-toast";
import {
  isValidIanaTimeZone,
  listSupportedIanaTimeZones,
} from "@/features/devices/ianaTimeZones";
import ScreensaverPanel from "@/features/screensavers/ScreensaverPanel";
import { canTargetSingleZone } from "@/features/screensavers/screensaversApi";
import ControlPanelActionRow from "../ControlPanelActionRow/ControlPanelActionRow";
import { SettingsForm } from "../SettingsForm/SettingsForm";
import MusicChannelPanel from "../MusicChannelPanel/MusicChannelPanel";
import styles from "./DevicesControlPanel.module.scss";
import { ConfirmButtonId } from "./types";

interface Props {
  selectedOption: BrandSoftwareTypeOption;
  ids: string[];
  /** Zone for Fleet channel assignment (device.zone_id). */
  zoneId?: string | null;
  onClickUpdate: () => void;
  onClickRestart: () => void;
  onClickReboot: () => void;
  onSetTimezone?: (timeZone: string) => void;
  /** Device-reported / confirmed OS time zone (display only — never auto-applied). */
  deviceTimeZone?: string | null;
  deviceTimeZoneConfirmed?: boolean | null;
  isMutatingUpdate: boolean;
  isMutatingRestart: boolean;
  isMutatingReboot: boolean;
  isMutatingTimezone?: boolean;
  commandsEnabled: boolean;
  commandsDisabledNotice?: string;
  /** Screensaver (KrossKast OS) — staging only, one Zone. */
  screensaverCommandsEnabled?: boolean;
  selectedScreensaverId?: string | null;
  /** Durable package-active contentId on device (Fleet SoT). */
  activeContentId?: string | null;
  activeReleaseId?: string | null;
  /** Device-confirmed screensaver enabled (Fleet `screensaver_enabled`). */
  screensaverEnabled?: boolean | null;
  onSelectScreensaver?: (id: string | null) => void;
  onScreensaverApply?: () => void;
  onScreensaverEnable?: () => void;
  onScreensaverDisable?: () => void;
  onScreensaverSoftwareUpdate?: () => void;
  isMutatingScreensaver?: boolean;
  isMutatingScreensaverSoftware?: boolean;
  /**
   * When set, poll Fleet package GETs so Installed updates after OTA
   * without a browser refresh or manual Refresh Installed Versions.
   */
  softwareOtaSyncTarget?: SoftwareInventoryOtaSyncTarget | null;
  onSoftwareOtaSyncSettled?: () => void;
  /** Fleet device.status Offline → Refresh explains verification unavailable. */
  deviceAppearsOffline?: boolean;
  /** Remote Console modal currently open. Never assume mount === open. */
  consoleOpen?: boolean;
}

const DevicesControlPanel: React.FC<Props> = ({
  selectedOption,
  ids,
  zoneId = null,
  onClickUpdate,
  onClickRestart,
  onClickReboot,
  onSetTimezone,
  deviceTimeZone = null,
  deviceTimeZoneConfirmed = null,
  isMutatingUpdate,
  isMutatingRestart,
  isMutatingReboot,
  isMutatingTimezone = false,
  commandsEnabled,
  commandsDisabledNotice,
  screensaverCommandsEnabled = false,
  selectedScreensaverId = null,
  activeContentId = null,
  activeReleaseId = null,
  screensaverEnabled = null,
  onSelectScreensaver,
  onScreensaverApply,
  onScreensaverEnable,
  onScreensaverDisable,
  onScreensaverSoftwareUpdate,
  isMutatingScreensaver = false,
  isMutatingScreensaverSoftware = false,
  softwareOtaSyncTarget = null,
  onSoftwareOtaSyncSettled,
  deviceAppearsOffline = false,
  consoleOpen = false,
}) => {
  const { UPDATE, RESTART, REBOOT } = ConfirmButtonId;
  /** Pending operator selection only — never treated as applied until Apply succeeds. */
  const [pendingTimeZone, setPendingTimeZone] = useState<string | undefined>(
    undefined
  );
  const timeZoneOptions = useMemo(
    () =>
      listSupportedIanaTimeZones().map((tz) => ({
        value: tz,
        label: tz,
      })),
    []
  );
  const [isRefreshPending, setIsRefreshPending] = useState(false);
  const refreshCancelledRef = useRef(false);
  const inventoryGenerationRef = useRef(createInventoryRequestGeneration());
  const [documentHidden, setDocumentHidden] = useState(() =>
    typeof document !== "undefined"
      ? document.visibilityState === "hidden"
      : false
  );

  const buttonsAreDisabled = useMemo(() => {
    return isMutatingUpdate || isMutatingRestart || isMutatingReboot;
  }, [isMutatingUpdate, isMutatingRestart, isMutatingReboot]);

  const [confirmButtonIds, setConfirmButtonIds] = useState<ConfirmButtonId[]>(
    []
  );

  const singleZone = canTargetSingleZone(ids);
  const packageDeviceId = singleZone ? ids[0] : "";
  const showPlayerPackage = selectedOption.value === "krosskast";
  const showAssistantPackage = selectedOption.value === "assistant";
  const softwareOtaSyncActive = softwareOtaSyncTarget != null;
  const inventoryPollMs = resolveSoftwareInventoryFleetPollingIntervalMs({
    singleZoneSelected: singleZone,
    consoleOpen,
    documentHidden,
    otaSyncActive: softwareOtaSyncActive,
    deviceAppearsOffline,
  });
  const syncStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    inventoryGenerationRef.current.bump();
  }, [packageDeviceId]);

  useEffect(() => {
    if (softwareOtaSyncActive) {
      if (syncStartedAtRef.current == null) {
        syncStartedAtRef.current = Date.now();
      }
    } else {
      syncStartedAtRef.current = null;
    }
  }, [softwareOtaSyncActive, softwareOtaSyncTarget]);

  const fetchPlayerLane = shouldFetchSoftwareInventoryLane({
    singleZoneSelected: singleZone,
    consoleOpen,
    laneActive: showPlayerPackage,
    otaSyncActive: softwareOtaSyncActive,
    laneIsOtaTarget: softwareOtaSyncTarget === "player",
  });
  const fetchAssistantLane = shouldFetchSoftwareInventoryLane({
    singleZoneSelected: singleZone,
    consoleOpen,
    laneActive: showAssistantPackage,
    otaSyncActive: softwareOtaSyncActive,
    laneIsOtaTarget: softwareOtaSyncTarget === "assistant",
  });
  const fetchScreensaverLane = shouldFetchSoftwareInventoryLane({
    singleZoneSelected: singleZone,
    consoleOpen,
    // ScreensaverPanel owns the visible screensaver query; subscribe here
    // only for OTA settle when that lane is the sync target.
    laneActive: false,
    otaSyncActive: softwareOtaSyncActive,
    laneIsOtaTarget: softwareOtaSyncTarget === "screensaver",
  });

  const {
    data: playerPackage,
    isFetching: playerPackageFetching,
    refetch: refetchPlayerPackage,
  } = useGetPlayerPackageStatusQuery(
    { deviceId: packageDeviceId },
    {
      skip: !packageDeviceId || !fetchPlayerLane,
      pollingInterval: inventoryPollMs,
      ...SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS,
    }
  );

  const {
    data: assistantPackage,
    isFetching: assistantPackageFetching,
    refetch: refetchAssistantPackage,
  } = useGetAssistantPackageStatusQuery(
    { deviceId: packageDeviceId },
    {
      skip: !packageDeviceId || !fetchAssistantLane,
      pollingInterval: inventoryPollMs,
      ...SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS,
    }
  );

  const { data: screensaverPackage, refetch: refetchScreensaverPackage } =
    useGetScreensaverPackageStatusQuery(
      { deviceId: packageDeviceId },
      {
        skip: !packageDeviceId || !fetchScreensaverLane,
        pollingInterval: inventoryPollMs,
        ...SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS,
      }
    );

  // Tab hidden → pause timers. Visible again: one Fleet GET only while OTA syncs.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibility = () => {
      const hidden = document.visibilityState === "hidden";
      setDocumentHidden(hidden);
      if (hidden || !packageDeviceId || !singleZone || !softwareOtaSyncActive) {
        return;
      }
      const gen = inventoryGenerationRef.current.current();
      const safeRefetch = (fn: () => unknown) => {
        if (!inventoryGenerationRef.current.isCurrent(gen)) return;
        void fn();
      };
      if (fetchPlayerLane) safeRefetch(() => refetchPlayerPackage());
      if (fetchAssistantLane) safeRefetch(() => refetchAssistantPackage());
      if (fetchScreensaverLane) safeRefetch(() => refetchScreensaverPackage());
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [
    packageDeviceId,
    singleZone,
    softwareOtaSyncActive,
    fetchPlayerLane,
    fetchAssistantLane,
    fetchScreensaverLane,
    refetchPlayerPackage,
    refetchAssistantPackage,
    refetchScreensaverPackage,
  ]);

  // Settle on the OTA'd package lane — not whichever Remote Console tab is open.
  const syncFocusStatus =
    softwareOtaSyncTarget === "assistant"
      ? assistantPackage
      : softwareOtaSyncTarget === "player"
        ? playerPackage
        : softwareOtaSyncTarget === "screensaver"
          ? screensaverPackage
          : null;

  useEffect(() => {
    if (!softwareOtaSyncActive || syncStartedAtRef.current == null) return;

    const evaluate = () => {
      if (syncStartedAtRef.current == null) return;
      const cont = shouldContinueSoftwareInventoryOtaSync({
        syncActive: softwareOtaSyncActive,
        status: syncFocusStatus,
        startedAtMs: syncStartedAtRef.current,
      });
      if (!cont) {
        onSoftwareOtaSyncSettled?.();
      }
    };

    evaluate();
    // Timeout must fire even if package GET payloads stay unchanged.
    const timer = window.setInterval(evaluate, SOFTWARE_INVENTORY_OTA_POLL_MS);
    return () => window.clearInterval(timer);
  }, [
    softwareOtaSyncActive,
    softwareOtaSyncTarget,
    syncFocusStatus,
    onSoftwareOtaSyncSettled,
  ]);

  const [refreshSoftwareInventory] = useRefreshSoftwareInventoryMutation();
  const [fetchPlayerPackage] = useLazyGetPlayerPackageStatusQuery();
  const [fetchAssistantPackage] = useLazyGetAssistantPackageStatusQuery();
  const [fetchScreensaverPackage] = useLazyGetScreensaverPackageStatusQuery();

  // Cancel in-flight Refresh wait when Zone changes or panel unmounts.
  useEffect(() => {
    refreshCancelledRef.current = false;
    return () => {
      refreshCancelledRef.current = true;
    };
  }, [packageDeviceId]);

  const isRefreshingInventory = isRefreshPending;
  const playerInstalledDisplay = formatInstalledVersionLabel({
    status: playerPackage,
    loading: playerPackageFetching,
    singleZone,
  });
  const playerVerifiedCaption = formatInventoryVerificationCaption({
    status: playerPackage,
    loading: playerPackageFetching,
    singleZone,
  });
  const playerAvailableDisplay = formatAvailableVersionLabel(playerPackage);
  const playerUpToDate = isSoftwareStatusUpToDate(playerPackage);
  const playerUpdateOk =
    commandsEnabled &&
    singleZone &&
    isSoftwareUpdateEligible({ status: playerPackage }) &&
    !isMutatingUpdate &&
    !isRefreshingInventory;

  const assistantInstalledDisplay = formatInstalledVersionLabel({
    status: assistantPackage,
    loading: assistantPackageFetching,
    singleZone,
  });
  const assistantVerifiedCaption = formatInventoryVerificationCaption({
    status: assistantPackage,
    loading: assistantPackageFetching,
    singleZone,
  });
  const assistantAvailableDisplay =
    formatAvailableVersionLabel(assistantPackage);
  const assistantUpToDate = isSoftwareStatusUpToDate(assistantPackage);
  const assistantUpdateOk =
    commandsEnabled &&
    singleZone &&
    isSoftwareUpdateEligible({ status: assistantPackage }) &&
    !isMutatingUpdate &&
    !isRefreshingInventory;

  const getAppText = (): {
    version: string;
    description?: string;
  } => {
    switch (selectedOption.value) {
      case "krosskast":
        return {
          version: playerAvailableDisplay,
          description:
            "Installed Version is device-reported PackageManager identity. Available is the active approved KrossKast App release.",
        };
      case "assistant":
        return {
          version: assistantAvailableDisplay,
          description:
            "Updates Assistant via the Player (update_assistant_app on the KK channel). Installed version is device-reported package truth.",
        };
      default:
        return { version: "", description: "" };
    }
  };

  const handleRefreshInstalledVersions = async () => {
    if (!commandsEnabled || !singleZone || !packageDeviceId) {
      toast.error("Select exactly one Zone to refresh installed versions");
      return;
    }
    if (isRefreshPending) return;

    const baselineObservedAt = newestInventoryObservedAt([
      playerPackage,
      assistantPackage,
      screensaverPackage,
    ]);

    setIsRefreshPending(true);
    refreshCancelledRef.current = false;
    try {
      const outcome = await runManualSoftwareInventoryRefresh({
        deviceAppearsOffline,
        baselineObservedAt,
        publish: () =>
          refreshSoftwareInventory({ deviceId: packageDeviceId }).unwrap(),
        fetchStatuses: async () => {
          const [player, assistant, screensaver] = await Promise.all([
            fetchPlayerPackage({ deviceId: packageDeviceId }).unwrap(),
            fetchAssistantPackage({ deviceId: packageDeviceId }).unwrap(),
            fetchScreensaverPackage({ deviceId: packageDeviceId }).unwrap(),
          ]);
          if (showPlayerPackage) {
            await refetchPlayerPackage();
          }
          if (showAssistantPackage) {
            await refetchAssistantPackage();
          }
          return [player, assistant, screensaver];
        },
        isCancelled: () => refreshCancelledRef.current,
      });

      if (outcome === "confirmed") {
        toast.success(SOFTWARE_INVENTORY_REFRESH_COPY.confirmed);
      } else if (outcome === "cancelled") {
        // Zone changed / unmount — keep last known inventory silently.
      } else {
        toast.error(SOFTWARE_INVENTORY_REFRESH_COPY[outcome]);
      }
    } finally {
      setIsRefreshPending(false);
    }
  };

  const handleOnClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const id = e.currentTarget.id as ConfirmButtonId;

    setConfirmButtonIds(
      confirmButtonIds.includes(id)
        ? confirmButtonIds.filter((t) => t !== id)
        : [...confirmButtonIds, id]
    );
  };

  const {
    update: { canSubmit: canSubmitUpdate },
    restart: { canSubmit: canSubmitRestart },
    reboot: { canSubmit: canSubmitReboot },
  } = useMemo(() => {
    return {
      update: {
        canSubmit: confirmButtonIds.includes(UPDATE),
      },
      restart: {
        canSubmit: confirmButtonIds.includes(RESTART),
      },
      reboot: {
        canSubmit: confirmButtonIds.includes(REBOOT),
      },
    };
  }, [confirmButtonIds, REBOOT, RESTART, UPDATE]);

  useEffect(() => {
    if (selectedOption.value !== undefined) {
      setConfirmButtonIds([]);
    }
  }, [selectedOption.value]);

  const handleOnClickSubmit = (type: string) => () => {
    setConfirmButtonIds([]);

    switch (type) {
      case UPDATE:
        onClickUpdate();
        break;
      case RESTART:
        onClickRestart();
        break;
      default:
        onClickReboot();
        break;
    }
  };

  const renderSoftwareInventorySection = (args: {
    testIdPrefix: string;
    installedDisplay: string;
    verifiedCaption: ReturnType<typeof formatInventoryVerificationCaption>;
    availableDisplay: string;
    upToDate: boolean;
    updateOk: boolean;
    fetching: boolean;
    updateLabel: string;
  }) => (
    <div className="mb-2" data-testid={`${args.testIdPrefix}-software-section`}>
      <ControlPanelActionRow
        className={styles.firstActionRow}
        leftComponent={<Text size="md">Installed Version</Text>}
        rightComponent={
          <div data-testid={`${args.testIdPrefix}-software-installed`}>
            <Text size="md">
              {args.installedDisplay}
              {args.fetching && (
                <SyncOutlined className={styles.syncLoader} spin />
              )}
            </Text>
            {args.verifiedCaption && (
              <span
                className={styles.inventoryVerifiedCaption}
                title={args.verifiedCaption.exactAt || undefined}
                data-testid={`${args.testIdPrefix}-software-verified`}
              >
                {args.verifiedCaption.text}
              </span>
            )}
          </div>
        }
      />
      <ControlPanelActionRow
        leftComponent={<Text size="md">Available Version</Text>}
        rightComponent={
          <div data-testid={`${args.testIdPrefix}-software-available`}>
            <Text size="md">{args.availableDisplay}</Text>
          </div>
        }
      />
      <ControlPanelActionRow
        leftComponent={<Text size="md">Refresh Installed Versions</Text>}
        rightComponent={
          <div className="d-grid justify-content-end">
            <ButtonSmall
              type="button"
              disabled={
                !commandsEnabled ||
                !singleZone ||
                isRefreshingInventory ||
                isMutatingUpdate
              }
              onClick={handleRefreshInstalledVersions}
              data-testid={`${args.testIdPrefix}-software-refresh`}
            >
              <Text size="md">
                {isRefreshingInventory && (
                  <SyncOutlined
                    className={styles.refreshButtonSpinner}
                    spin
                  />
                )}
                {isRefreshingInventory ? "Refreshing…" : "Refresh"}
              </Text>
            </ButtonSmall>
          </div>
        }
      />
      {args.upToDate ? (
        <div
          className="alert alert-secondary py-2 mb-2"
          role="status"
          data-testid={`${args.testIdPrefix}-software-up-to-date`}
        >
          Software is up to date.
        </div>
      ) : (
        <ControlPanelActionRow
          borderBottom={false}
          leftComponent={
            <div className="d-flex align-items-center">
              {isMutatingUpdate && (
                <SyncOutlined className={styles.syncLoader} spin />
              )}
            </div>
          }
          rightComponent={
            <div className="d-grid justify-content-end">
              <ButtonSmall
                type="button"
                disabled={!args.updateOk}
                onClick={onClickUpdate}
                data-testid={`${args.testIdPrefix}-software-update`}
              >
                <Text size="md">{args.updateLabel}</Text>
              </ButtonSmall>
            </div>
          }
        />
      )}
    </div>
  );

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between">
        <div className="d-flex flex-column">
          <Text type="title" size="md">
            {selectedOption.label} Software
          </Text>
          <Text type="text" size="sm">
            {getAppText().description}
          </Text>
        </div>
        <div className="d-flex align-items-center">
          <Icon
            className={classNames({
              [styles.playerIcon]: selectedOption.value === "player",
            })}
            size={30}
            name={selectedOption.value}
            useImage={true}
          />
        </div>
      </div>
      <Text className={styles.zoneText} size="sm">
        Zone(s) Selected: {ids.length}
      </Text>
      {!commandsEnabled && commandsDisabledNotice && (
        <div
          className="alert alert-warning py-2 mb-2"
          role="status"
          data-testid="remote-console-commands-disabled"
        >
          {commandsDisabledNotice}
        </div>
      )}
      {selectedOption.value === "assistant" ? (
        <React.Fragment>
          {renderSoftwareInventorySection({
            testIdPrefix: "assistant",
            installedDisplay: assistantInstalledDisplay,
            verifiedCaption: assistantVerifiedCaption,
            availableDisplay: assistantAvailableDisplay,
            upToDate: assistantUpToDate,
            updateOk: assistantUpdateOk,
            fetching: assistantPackageFetching,
            updateLabel: "Update Assistant",
          })}
          {!singleZone && (
            <div
              className="alert alert-warning py-2 mb-2"
              role="status"
              data-testid="assistant-single-zone-required"
            >
              Select exactly one Zone to Update Assistant.
            </div>
          )}
          <ControlPanelActionRow
            borderBottom={false}
            leftComponent={
              <div className="d-flex align-items-center">
                <Text size="md">Restart App</Text>
                {isMutatingRestart && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-grid justify-content-end">
                <ConfirmSubmitButton
                  disabled={!commandsEnabled || buttonsAreDisabled}
                  id={RESTART}
                  isActive={canSubmitRestart}
                  onClick={handleOnClick}
                  onSubmit={handleOnClickSubmit("restart")}
                  text="Restart"
                />
              </div>
            }
          />
        </React.Fragment>
      ) : selectedOption.value === "krosskast" ? (
        <React.Fragment>
          {renderSoftwareInventorySection({
            testIdPrefix: "player",
            installedDisplay: playerInstalledDisplay,
            verifiedCaption: playerVerifiedCaption,
            availableDisplay: playerAvailableDisplay,
            upToDate: playerUpToDate,
            updateOk: playerUpdateOk,
            fetching: playerPackageFetching,
            updateLabel: "Update App",
          })}
          {!singleZone && (
            <div
              className="alert alert-warning py-2 mb-2"
              role="status"
              data-testid="player-single-zone-required"
            >
              Select exactly one Zone to Update App.
            </div>
          )}
          <ControlPanelActionRow
            borderBottom={false}
            leftComponent={
              <div className="d-flex align-items-center">
                <Text size="md">Restart App</Text>
                {isMutatingRestart && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-grid justify-content-end">
                <ConfirmSubmitButton
                  disabled={!commandsEnabled || buttonsAreDisabled}
                  id={RESTART}
                  isActive={canSubmitRestart}
                  onClick={handleOnClick}
                  onSubmit={handleOnClickSubmit("restart")}
                  text="Restart"
                />
              </div>
            }
          />
        </React.Fragment>
      ) : selectedOption.value !== "player" ? (
        <React.Fragment>
          <ControlPanelActionRow
            className={styles.firstActionRow}
            leftComponent={
              <div className="d-flex align-items-center">
                <Text size="md">Update App</Text>
                {isMutatingUpdate && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-flex align-items-center justify-content-end">
                <Text className="mx-3" size="md">
                  {getAppText().version}
                </Text>
                <ConfirmSubmitButton
                  disabled={!commandsEnabled || isMutatingUpdate}
                  id={ConfirmButtonId.UPDATE}
                  isActive={canSubmitUpdate}
                  onClick={handleOnClick}
                  onSubmit={onClickUpdate}
                  text="Update"
                />
              </div>
            }
          />
          <ControlPanelActionRow
            borderBottom={false}
            leftComponent={
              <div className="d-flex align-items-center">
                <Text size="md">Restart App</Text>
                {isMutatingRestart && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-grid justify-content-end">
                <ConfirmSubmitButton
                  disabled={!commandsEnabled || buttonsAreDisabled}
                  id={RESTART}
                  isActive={canSubmitRestart}
                  onClick={handleOnClick}
                  onSubmit={handleOnClickSubmit("restart")}
                  text="Restart"
                />
              </div>
            }
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <ControlPanelActionRow
            borderBottom={true}
            leftComponent={
              <div>
                <Text size="md">Device Time Zone</Text>
                <div className="mt-1" style={{ fontSize: 12, opacity: 0.85 }}>
                  Current:{" "}
                  {deviceTimeZone
                    ? `${deviceTimeZone}${
                        deviceTimeZoneConfirmed === true
                          ? " (confirmed)"
                          : " (reported)"
                      }`
                    : "Not reported"}
                </div>
                <div className="mt-2" style={{ minWidth: 280, maxWidth: 360 }}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Search IANA time zone…"
                    optionFilterProp="label"
                    options={timeZoneOptions}
                    value={pendingTimeZone}
                    disabled={
                      !commandsEnabled ||
                      buttonsAreDisabled ||
                      isMutatingTimezone
                    }
                    onChange={(value) => {
                      // Selection only — never auto-apply.
                      setPendingTimeZone(
                        typeof value === "string" && value ? value : undefined
                      );
                    }}
                    style={{ width: "100%" }}
                    aria-label="Pending device time zone"
                    data-testid="device-timezone-select"
                  />
                </div>
                {isMutatingTimezone && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-grid justify-content-end">
                <ButtonSmall
                  disabled={
                    !commandsEnabled ||
                    buttonsAreDisabled ||
                    isMutatingTimezone ||
                    !pendingTimeZone ||
                    !onSetTimezone ||
                    !isValidIanaTimeZone(pendingTimeZone)
                  }
                  onClick={() => {
                    if (
                      pendingTimeZone &&
                      onSetTimezone &&
                      isValidIanaTimeZone(pendingTimeZone)
                    ) {
                      onSetTimezone(pendingTimeZone);
                    }
                  }}
                >
                  Apply Time Zone
                </ButtonSmall>
              </div>
            }
          />
          <ControlPanelActionRow
            borderBottom={false}
            leftComponent={
              <div>
                <Text size="md">Reboot Device</Text>
                {isMutatingReboot && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-grid justify-content-end">
                <ConfirmSubmitButton
                  disabled={!commandsEnabled || buttonsAreDisabled}
                  id={REBOOT}
                  isActive={canSubmitReboot}
                  onClick={handleOnClick}
                  onSubmit={handleOnClickSubmit("reboot")}
                  text="Reboot"
                />
              </div>
            }
          />
        </React.Fragment>
      )}
      {selectedOption.value === "krosskast" && (
        <>
          <MusicChannelPanel
            zoneId={singleZone ? zoneId : null}
            deviceId={singleZone ? ids[0] : null}
            commandsEnabled={commandsEnabled}
            consoleOpen={consoleOpen}
            deviceAppearsOffline={deviceAppearsOffline}
          />
          <SettingsForm
            disabled={ids.length > 1}
            ids={ids}
            command={selectedOption.value}
            title={`${selectedOption.label} Settings`}
            commandsEnabled={commandsEnabled}
          />
        </>
      )}
      {selectedOption.value === "player" &&
        onSelectScreensaver &&
        onScreensaverApply &&
        onScreensaverEnable &&
        onScreensaverDisable && (
          <div className="mt-3">
            <ScreensaverPanel
              ids={ids}
              consoleOpen={consoleOpen}
              commandsEnabled={screensaverCommandsEnabled}
              isMutating={isMutatingScreensaver}
              isMutatingSoftware={isMutatingScreensaverSoftware}
              softwareOtaSyncActive={
                softwareOtaSyncTarget === "screensaver"
              }
              deviceAppearsOffline={deviceAppearsOffline}
              selectedScreensaverId={selectedScreensaverId}
              activeContentId={activeContentId}
              activeReleaseId={activeReleaseId}
              screensaverEnabled={screensaverEnabled}
              onSelectScreensaver={onSelectScreensaver}
              onApply={onScreensaverApply}
              onEnable={onScreensaverEnable}
              onDisable={onScreensaverDisable}
              onUpdateSoftware={onScreensaverSoftwareUpdate}
            />
          </div>
        )}
    </React.Fragment>
  );
};

export default DevicesControlPanel;
